import { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Plus, Search, Pen, Trash2, X as IconX, Upload, Clock, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { CustomSelect } from '../../components/CustomSelect';
import { SideDrawer } from '../../components/SideDrawer';
import { ImageCarousel } from '../../components/ImageCarousel';
import {
  catalogServicesOf, sSubCategoriesOf, sCategoriesOf, gstRatesOf,
  addCatalogService, updateCatalogService, deleteCatalogService,
  findGst, findSSubCategory, findSCategory,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { formatCategoryName, formatNumber, formatInteger } from '../../utils/formatters';
import { compressImage } from '../../utils/image';
import { IconAction, RowActions } from '../../components/RowActions';
import { ViewToggle } from '../../components/ViewToggle';

const blank = (orgId) => ({
  id: null,
  orgId,
  subCategoryId: '',
  name: '',
  description: '',
  images: [],
  gstId: '',
  // Empty means follow the shop. A salon quoting a round 500 has the same
  // problem a shop printing an MRP does.
  taxMode: '',
  price: 0,
  duration: 30,
});

const ServiceForm = ({ open, draft, setDraft, onClose, onSubmit, mode, subCats, cats, gstList, accent, showNotification }) => {
  const fileRef = useRef(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Service name is required.';
    if (!draft.subCategoryId) newErrors.subCategoryId = 'Please select a sub-category.';
    if (draft.price === '' || draft.price === null) newErrors.price = 'Price is required.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (typeof showNotification !== 'undefined' && showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit({ ...draft, gstId: draft.gstId || null, taxMode: draft.taxMode || '', price: Number(draft.price) });
  };

  // Uncompressed like the others were, and services can carry several images
  // each, so this one multiplies.
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const image = await compressImage(file);
    setDraft((prev) => ({ ...prev, images: [...prev.images, image] }));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="w-full h-full flex flex-col flex-1 bg-white rounded-lg shadow-sm border border-[var(--theme-tertiary)] overflow-hidden"
    >
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0 bg-slate-50">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} aria-label="Go back" className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit service' : 'New service'}</h3>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 mt-1">S-Category → S-Sub-Category → Service</p>
          </div>
        </div>
      </header>

      <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Images</span>
          <div className="flex items-center gap-4 overflow-x-auto custom-scrollbar pb-2">
            {draft.images.map((img, idx) => (
              <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[var(--theme-tertiary)] shrink-0 group">
                <img src={img} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, images: draft.images.filter((_, i) => i !== idx) })}
                  className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <IconX size={10} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-20 h-20 rounded-lg border border-dashed border-[var(--theme-tertiary)] flex flex-col items-center justify-center gap-1 hover:border-[var(--theme-primary)] hover:bg-[var(--theme-primary-soft)] hover:text-[var(--theme-primary)] text-slate-400 shrink-0 transition-colors"
            >
              <Upload size={16} aria-hidden="true" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">Upload</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
          </div>
        </div>

        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            S-Sub-Category <span className="text-rose-500 ml-1">*</span>
          </span>
          <CustomSelect
            value={draft.subCategoryId}
            onChange={(v) => {
              setDraft({ ...draft, subCategoryId: v });
              if (errors.subCategoryId) setErrors({ ...errors, subCategoryId: undefined });
            }}
            options={subCats.map((sc) => {
              const cat = cats.find((c) => c.id === sc.categoryId);
              return { value: sc.id, label: `${cat ? `${cat.name} > ` : ''}${sc.name}` };
            })}
            placeholder="— Select —"
            className={`w-full bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 ${errors.subCategoryId ? 'border border-rose-400' : 'border-gray-100'}`}
          />
          {errors.subCategoryId && <p className="text-[11px] font-medium text-rose-500">{errors.subCategoryId}</p>}
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Service name <span className="text-rose-500 ml-1">*</span>
          </span>
          <input type="text" value={draft.name} onChange={(e) => {
            setDraft({ ...draft, name: formatCategoryName(e.target.value) });
            if (errors.name) setErrors({ ...errors, name: undefined });
          }} placeholder="e.g. Police Cutting"
            className={`w-full rounded-lg bg-gray-50 border ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
          {errors.name && <p className="text-[11px] font-medium text-rose-500">{errors.name}</p>}
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Description</span>
          <textarea rows={2} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] resize-none" />
        </label>
        {/* Whether this price already contains its GST. Blank follows the
            shop-wide setting, on the GST tab. */}
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Tax on the price</span>
          <CustomSelect
            value={draft.taxMode || ''}
            onChange={(v) => setDraft({ ...draft, taxMode: v })}
            placeholder="Follow the shop"
            options={[
              { value: '', label: 'Follow the shop' },
              { value: 'exclusive', label: 'GST on top of this price' },
              { value: 'inclusive', label: 'This price includes GST' },
            ]}
            className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
          />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">GST</span>
            <CustomSelect
              value={draft.gstId}
              onChange={(v) => setDraft({ ...draft, gstId: v })}
              options={gstList.map((g) => ({ value: g.id, label: g.name }))}
              placeholder="— GST —"
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Price (₹) <span className="text-rose-500 ml-1">*</span>
            </span>
            <input type="text" value={draft.price}
              onChange={(e) => {
                let v = formatNumber(e.target.value);
                if (v.length > 1 && v.startsWith('0') && v[1] !== '.') {
                  v = v.replace(/^0+/, '');
                }
                setDraft({ ...draft, price: v });
                if (errors.price) setErrors({ ...errors, price: undefined });
              }}
              className={`w-full rounded-lg bg-gray-50 border ${errors.price ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
            {errors.price && <p className="text-[11px] font-medium text-rose-500">{errors.price}</p>}
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Duration (m)</span>
            <input type="text" value={draft.duration}
              onChange={(e) => setDraft({ ...draft, duration: formatInteger(e.target.value) })}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
        </div>
      </div>

      <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0 bg-slate-50">
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
          style={{ background: accent }}
        >
          {mode === 'edit' ? 'Save service' : 'Create service'}
        </button>
      </footer>
    </motion.section>
  );
};

ServiceForm.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  subCats: PropTypes.array.isRequired,
  cats: PropTypes.array.isRequired,
  gstList: PropTypes.array.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};

export const ServicesTab = ({ org, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);

  const subCats = useMemo(() => sSubCategoriesOf(org.id), [org.id, dataVersion]);
  const cats = useMemo(() => sCategoriesOf(org.id), [org.id, dataVersion]);
  const gstList = useMemo(() => gstRatesOf(org.id), [org.id, dataVersion]);

  const list = useMemo(() => {
    const all = catalogServicesOf(org.id);
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [org.id, search, version, dataVersion]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(list.length / perPage));
  const start = (page - 1) * perPage;
  const pageList = list.slice(start, start + perPage);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id)); setMode('create'); setModalOpen(true); };
  const openEdit = (s) => {
    const draftData = { ...s };
    if (!draftData.images) draftData.images = [];
    setDraft(draftData);
    setMode('edit');
    setModalOpen(true);
  };

  const submit = (record) => {
    const final = { ...record, orgId: org.id };
    if (mode === 'create') addCatalogService(final);
    else updateCatalogService(record.id, final);
    if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} ${final.name}`, 'success');
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteCatalogService(confirmDelete.id);
    if (showNotification) showNotification(`Removed ${confirmDelete.name}`, 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  if (modalOpen) {
    return (
      <div className="flex flex-col h-full w-full">
        <ServiceForm
          open={modalOpen}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
          mode={mode}
          subCats={subCats}
          cats={cats}
          gstList={gstList}
          accent={accent}
          showNotification={showNotification}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-[#0d0d0d] outline-none transition-colors" />
          </div>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <button type="button" onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg shrink-0"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
          <Plus size={14} aria-hidden="true" /> New service
        </button>
      </header>
      {viewMode === 'grid' ? (
        <section className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {pageList.map((s) => {
            const sub = findSSubCategory(s.subCategoryId);
            const cat = sub ? findSCategory(sub.categoryId) : null;
            const gst = findGst(s.gstId);
            return (
              <motion.article key={s.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden flex flex-col hover:border-[var(--theme-primary-tint)] transition-colors">
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <ImageCarousel images={s.images} alt={s.name} />
                </div>
                <div className="p-3.5 flex flex-col gap-2 flex-1 min-w-0">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate" style={{ color: accent }}>
                      {cat ? `${cat.name} › ` : ''}{sub ? sub.name : '—'}
                    </p>
                    <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{s.name}</p>
                    {s.description && <p className="text-[10px] font-medium text-slate-500 line-clamp-1 mt-0.5">{s.description}</p>}
                  </div>
                  
                  <div className="flex items-center justify-between gap-1 text-[10px] font-extrabold uppercase tracking-[0.05em] w-full min-h-[18px]">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="text-[#0d0d0d]">₹{(s.price || 0).toFixed(2)}</span>
                      <span className="text-slate-300">•</span>
                      <span style={{ color: accent }}>GST {gst ? `${gst.rate}%` : '0%'}</span>
                    </div>
                  </div>

                  <footer className="flex items-center justify-end gap-2 mt-auto pt-1.5 border-t border-[var(--theme-tertiary)]">
                    <button type="button" aria-label={`Edit ${s.name}`} onClick={() => openEdit(s)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
                      <Pen size={12} /> Edit
                    </button>
                    <button type="button" aria-label={`Remove ${s.name}`} onClick={() => setConfirmDelete(s)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
                      <Trash2 size={12} /> Remove
                    </button>
                  </footer>
                </div>
              </motion.article>
            );
          })}
          {pageList.length === 0 && (
            <div className="col-span-full py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No services found.</p>
              <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                <Plus size={12} /> Create the first one
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left">
              <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 w-16 whitespace-nowrap">Image</th>
                  <th className="px-4 py-3 whitespace-nowrap">Service Name</th>
                  <th className="px-4 py-3 whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 whitespace-nowrap">Sub-Category</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Price</th>
                  <th className="px-4 py-3 whitespace-nowrap text-center">Duration</th>
                  <th className="px-4 py-3 whitespace-nowrap text-center">GST</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageList.map((s) => {
                  const sub = findSSubCategory(s.subCategoryId);
                  const cat = sub ? findSCategory(sub.categoryId) : null;
                  const gst = findGst(s.gstId);
                  return (
                    <motion.tr
                      key={s.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--theme-tertiary)] last:border-0 transition-colors hover:bg-slate-50 group"
                    >
                      <td className="px-4 py-2">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--theme-secondary)] flex items-center justify-center shrink-0 border border-slate-100">
                          {s.images?.length > 0 ? (
                            <img src={s.images[0]} alt={s.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                              <span className="text-[10px] font-bold">NO IMG</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-[13px] font-extrabold text-[#0d0d0d] whitespace-nowrap">{s.name}</p>
                        {s.description && <p className="text-[11px] font-medium text-slate-500 line-clamp-1 max-w-[200px] mt-0.5">{s.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-[11px] font-bold whitespace-nowrap text-slate-600">
                        {cat ? cat.name : '—'}
                      </td>
                      <td className="px-4 py-3 text-[11px] font-bold whitespace-nowrap" style={{ color: accent }}>
                        {sub ? sub.name : '—'}
                      </td>
                      <td className="px-4 py-3 text-[13px] font-extrabold text-[#0d0d0d] whitespace-nowrap text-right">
                        ₹{(s.price || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-[12px] font-semibold text-slate-600 whitespace-nowrap text-center">
                        {s.duration || 0}m
                      </td>
                      <td className="px-4 py-3 text-[11px] font-bold whitespace-nowrap text-center text-slate-500">
                        {gst ? `${gst.rate}%` : '0%'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <RowActions>
                          <IconAction icon={Pen} label={`Edit ${s.name}`} title="Edit" onClick={() => openEdit(s)} />
                          <IconAction icon={Trash2} tone="danger" label={`Remove ${s.name}`} title="Remove" onClick={() => setConfirmDelete(s)} />
                        </RowActions>
                      </td>
                    </motion.tr>
                  );
                })}
                {pageList.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No services found.</p>
                      <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                        <Plus size={12} /> Create the first one
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm">
          <span className="text-[11px] font-medium text-slate-500">
            Showing {list.length === 0 ? 0 : start + 1} to {Math.min(start + perPage, list.length)} of {list.length} entries
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-semibold text-[#0d0d0d]">Page {page} of {totalPages}</span>
            </div>
            <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">This service will be removed from the catalog.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ServicesTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
